import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Settings as SettingsIcon, Palette, Shield, Trash2, Download, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useSettings } from './useSettings';

export function Settings() {
  const { settings, isLoading, updateSettings } = useSettings();
  const { toast } = useToast();

  const handleSettingChange = (key: keyof any, value: any) => {
    if (!settings) return;
    updateSettings({ [key]: value });
  };

  const handleResetData = () => {
    toast({
      title: 'Reset Not Available',
      description: 'Use DB scripts or reseed (force) to reset in API mode.',
    });
  };

  const handleExportSettings = () => {
    if (!settings) return;
    
    const exportData = {
      settings,
      exportDate: new Date().toISOString(),
      version: "1.0"
    };
    
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `workfinity-settings-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    toast({
      title: "Settings Exported",
      description: "Your settings have been downloaded as a JSON file.",
    });
  };

  if (isLoading || !settings) {
    return (
      <div className="bg-gray-50 min-h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-workfinity-600" />
          <p className="mt-2 text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-full">
      <div className="mb-4 md:mb-6">
        <h2 className="text-xl md:text-2xl font-semibold text-gray-900">Settings</h2>
        <p className="text-gray-600 mt-1 text-sm md:text-base">Configure system behavior and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Data Management Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <SettingsIcon className="h-5 w-5" />
              <span>Data Management</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Canonicalize Codes</Label>
                <p className="text-sm text-gray-500">
                  Automatically convert codes to uppercase
                </p>
              </div>
              <Switch
                checked={settings.canonicalizeCode}
                onCheckedChange={(checked) => handleSettingChange('canonicalizeCode', checked)}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Enforce Unique Names</Label>
                <p className="text-sm text-gray-500">
                  Prevent duplicate names within the same type and parent
                </p>
              </div>
              <Switch
                checked={settings.enforceUniqueNames}
                onCheckedChange={(checked) => handleSettingChange('enforceUniqueNames', checked)}
              />
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label className="text-base">Auto Save</Label>
                <p className="text-sm text-gray-500">
                  Automatically save changes
                </p>
              </div>
              <Switch
                checked={settings.autoSave}
                onCheckedChange={(checked) => handleSettingChange('autoSave', checked)}
              />
            </div>
          </CardContent>
        </Card>

        {/* AI/NLPS Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5" />
              <span>AI / NLPS Configuration</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="nlpProvider">NLP Provider</Label>
              <Select 
                value={settings.nlpProvider} 
                onValueChange={(value) => handleSettingChange('nlpProvider', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LocalRules">Local Rules</SelectItem>
                  <SelectItem value="NLPS_API">NLPS API</SelectItem>
                  <SelectItem value="Proxy">Proxy</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                Choose the natural language processing provider
              </p>
            </div>

            {settings.nlpProvider !== 'LocalRules' && (
              <div className="space-y-2">
                <Label htmlFor="apiEndpoint">API Endpoint</Label>
                <Input
                  id="apiEndpoint"
                  type="url"
                  placeholder="https://api.workfinity.com/nlps"
                  value={settings.apiEndpoint || ''}
                  onChange={(e) => handleSettingChange('apiEndpoint', e.target.value)}
                />
                <p className="text-sm text-gray-500">
                  API endpoint for external NLP services
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Appearance Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <span>Appearance</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="theme">Theme</Label>
              <Select 
                value={settings.theme} 
                onValueChange={(value) => handleSettingChange('theme', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light</SelectItem>
                  <SelectItem value="dark">Dark</SelectItem>
                  <SelectItem value="system">System</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-gray-500">
                Choose your preferred color scheme
              </p>
            </div>

            <div className="bg-workfinity-50 border border-workfinity-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-workfinity-900 mb-2">Brand Colors</h4>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 bg-workfinity-500 rounded"></div>
                  <span className="text-sm text-workfinity-800">Primary: #0A5BD3</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* System Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <SettingsIcon className="h-5 w-5" />
              <span>System Actions</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Button 
                onClick={handleExportSettings}
                variant="outline" 
                className="w-full justify-start"
              >
                <Download className="h-4 w-4 mr-2" />
                Export Settings
              </Button>
              <p className="text-sm text-gray-500">
                Download your current settings as a JSON file
              </p>
            </div>

            <Separator />

            <div className="space-y-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="destructive" 
                    className="w-full justify-start"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Reset All Data
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone. This will permanently delete all your 
                      types, codes, audit logs, and settings data.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleResetData} className="bg-red-600 hover:bg-red-700">
                      Reset All Data
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              <p className="text-sm text-gray-500">
                Permanently delete all data and reset to defaults
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Legacy System Link */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Legacy System</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 mb-2">
                Access the original WorkFINITY® BPRM system
              </p>
              <a 
                href="https://ipcs-bprm.workfinity.com/login.aspx?encrypt=1&key=NjYwZjRhMzYtNzJjZi00YzViLWIxOTAtOTBlNmRmZmU2Mzg0" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-workfinity-600 hover:text-workfinity-700 text-sm underline"
              >
                https://ipcs-bprm.workfinity.com/login.aspx?encrypt=1&key=...
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}